import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AiFillStar } from "react-icons/ai";
import toast from "react-hot-toast";
import bookingApi from "../api/booking/BookingApi";
import Layout from "../Layout";
import Loading from "../Util/Loading";

const formatDuration = (mins) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours && minutes) return `${hours} hr ${minutes} mins`;
  if (hours) return `${hours} hr`;
  return `${minutes} mins`;
};

const extractMinutes = (desc) => {
  const lower = desc?.toLowerCase() || "";
  const hrMatch = lower.match(/(\d+)\s*hr/);
  const minMatch = lower.match(/(\d+)\s*min/);
  const hr = hrMatch ? parseInt(hrMatch[1], 10) : 0;
  const min = minMatch ? parseInt(minMatch[1], 10) : 0;
  return hr * 60 + min;
};

const AppointmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const getBookingDetail = async () => {
    try {
      const { response } = await bookingApi.getBookingById({ id });
      if (response?.data?.booking) {
        setBooking(response.data.booking);
      } else {
        toast.error("Booking not found");
        navigate("/appointments");
      }
    } catch (error) {
      toast.error("Error loading booking");
      console.error(error);
      navigate("/appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getBookingDetail();
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!booking) return null;

  const {
    customer_info,
    selectedNailServices,
    selectedBeautyServices,
    selectedProfessional,
    selectedDateTime,
    note,
  } = booking;

  const SectionCard = ({ title, children }) => (
    <div className="bg-white/10 p-4 rounded-2xl shadow border border-border">
      <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div>{children}</div>
    </div>
  );

  const renderServiceListWithTime = (services) => {
    if (!services.length)
      return <p className="text-base text-gray-400 italic">None selected</p>;

    const totalMins = services.reduce(
      (acc, s) => acc + extractMinutes(s.desc),
      0
    );

    return (
      <>
        {services.map((item, i) => (
          <div key={i} className="flex items-start space-x-2 mb-2">
            <div className="w-6 h-6 bg-yellowlight rounded-md flex items-center justify-center mt-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-yellowdark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-white">
              {item.name}
              {item.desc && (
                <span className="text-sm text-gray-400"> ({item.desc})</span>
              )}
            </p>
          </div>
        ))}
        <p className="text-right text-yellowlight font-medium mt-2">
          Total: {formatDuration(totalMins)}
        </p>
      </>
    );
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 text-white space-y-6">
        <h2 className="text-2xl font-bold text-yellowlight border-b border-border pb-4">
          Appointment Details
        </h2>

        <SectionCard title="Nail Services">
          {renderServiceListWithTime(selectedNailServices)}
        </SectionCard>

        <SectionCard title="Beauty Services">
          {renderServiceListWithTime(selectedBeautyServices)}
        </SectionCard>

        <SectionCard title="Professional">
          {selectedProfessional ? (
            <div className="flex items-center space-x-3 mt-2">
              <img
                src={selectedProfessional?.photo || "/default-avatar.png"}
                alt={selectedProfessional.name}
                className="w-10 h-10 rounded-full object-cover border border-yellowlight"
              />
              <div className="flex flex-col">
                <p className="text-lg font-semibold text-white">
                  {selectedProfessional.name}
                </p>
                {selectedProfessional.role && (
                  <p className="text-sm text-gray-400">
                    {selectedProfessional.role}
                  </p>
                )}
                <span className="flex items-center text-yellow-400 text-sm mt-1">
                  <AiFillStar className="mr-1" />
                </span>
              </div>
            </div>
          ) : (
            <p className="text-base text-gray-400 italic">
              No professional assigned
            </p>
          )}
        </SectionCard>

        <SectionCard title="Appointment Time">
          <p className="text-lg font-semibold text-white">
            {formatDateTime(selectedDateTime)}
          </p>
        </SectionCard>

        <SectionCard title="Customer Info">
          <ul className="space-y-1">
            <li>
              <span className="text-sm text-gray-400">Name:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.name || "—"}
              </span>
            </li>
            <li>
              <span className="text-sm text-gray-400">Email:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.email || "—"}
              </span>
            </li>
            <li>
              <span className="text-sm text-gray-400">Phone:</span>{" "}
              <span className="text-lg font-semibold text-white">
                {customer_info?.phone || "—"}
              </span>
            </li>
          </ul>
        </SectionCard>

        <SectionCard title="Notes">
          <p className="text-lg font-semibold text-white">{note || "—"}</p>
        </SectionCard>
      </div>
    </Layout>
  );
};

export default AppointmentDetail;
